import { useEffect, useState } from 'react';

import { PageShell } from '../components/layout/PageShell.js';
import { Stamp } from '../components/ui/Stamp.js';
import { useRoute } from '../lib/route.js';
import {
  listSavedPosts,
  type SavedPostsListItem,
} from '../lib/posts-client.js';
import { SmartApplyApiError } from '../lib/api-client.js';

export function SavedPostsRoute() {
  const { navigate } = useRoute();
  const [items, setItems] = useState<SavedPostsListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listSavedPosts()
      .then((res) => {
        if (alive) setItems(res.items);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao listar.');
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <PageShell>
      <header className="mb-10">
        <p className="mb-4 inline-block">
          <Stamp>Histórico</Stamp>
        </p>
        <h1 className="font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px]">
          Drafts salvos
          <span className="text-[var(--accent-saffron)]">.</span>
        </h1>
        <p className="mt-3 max-w-2xl font-sans text-sm text-[var(--ink-secondary)]">
          Conjuntos de drafts que você salvou explicitamente. Por padrão, drafts
          ficam só na sessão.
        </p>
      </header>

      {error && (
        <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
      )}

      {items === null && !error && (
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          carregando...
        </p>
      )}

      {items && items.length === 0 && (
        <p className="font-sans text-base text-[var(--ink-secondary)]">
          Nenhum draft salvo ainda.{' '}
          <a href="#/posts" className="text-[var(--accent-saffron)] underline underline-offset-4">
            Gerar drafts
          </a>
          .
        </p>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-3">
          {items.map((item) => (
            <li
              key={item.id}
              className="card-paper rounded-sm px-5 py-4"
              style={{
                borderLeft: `3px solid ${
                  item.sourceType === 'topic'
                    ? 'var(--accent-saffron)'
                    : 'var(--brand-ink)'
                }`,
              }}
            >
              <button
                type="button"
                onClick={() => navigate(`/posts/saved/${item.id}`)}
                className="block w-full text-left"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
                    {item.sourceType === 'topic' ? 'tópico' : 'a partir de link/texto'}
                    {' · '}
                    <span className="tabular-nums">{item.draftCount}</span> drafts
                    {item.selectedDraftId ? ' · ✓ marcado' : ''}
                  </p>
                  <span className="font-mono text-[10px] tabular-nums text-[var(--ink-muted)]">
                    {new Date(item.createdAt).toLocaleString('pt-BR')}
                  </span>
                </div>
                <p className="mt-1 font-serif text-base text-[var(--ink-primary)]">
                  {item.label || truncate(item.sourceInput, 80) || '(sem rótulo)'}
                </p>
                <p className="mt-1 font-mono text-[10px] text-[var(--ink-muted)]">
                  modelo · {item.modelUsed}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}...` : s;
}
